/**
 * Shared utilities for detecting and categorizing session errors.
 * Used by both createSession.ts and SessionProvider.tsx
 */

const AUTH_FAILURE_PATTERNS = [
  'Invalid session ID',
  'SessionNotFound',
  'invalid session',
] as const;

const NEW_LOGON_PATTERN = 'NewLogonDetected';

const SESSION_TIMEOUT_PATTERN = 'SessionTimeout';

const CONNECTIVITY_PATTERNS = [
  'timeout',
  'Timeout',
  'connection',
  'Connection',
  'network',
  'Network',
] as const;

/**
 * Check if an error message indicates a definite authentication failure.
 * These errors should result in immediate logout.
 */
export function isAuthFailure(errorMessage: string): boolean {
  return AUTH_FAILURE_PATTERNS.some((pattern) => errorMessage.includes(pattern));
}

/**
 * Check if an error message indicates a new login from another session.
 */
export function isNewLogonError(errorMessage: string): boolean {
  return errorMessage.includes(NEW_LOGON_PATTERN);
}

/**
 * Check if an error message indicates a session timeout.
 */
export function isSessionTimeoutError(errorMessage: string): boolean {
  return errorMessage.includes(SESSION_TIMEOUT_PATTERN);
}

/**
 * Check if an error message indicates a connectivity issue.
 * These errors may be transient and worth retrying.
 */
export function isConnectivityIssue(errorMessage: string): boolean {
  return CONNECTIVITY_PATTERNS.some((pattern) => errorMessage.includes(pattern));
}

/**
 * Categorize an error message for Sentry tagging.
 */
export function categorizeErrorMessage(errorMessage: string): string {
  if (errorMessage.includes('Invalid session ID') || errorMessage.includes('invalid session')) {
    return 'invalid_session';
  }
  if (errorMessage.includes(NEW_LOGON_PATTERN)) {
    return 'new_logon_detected';
  }
  if (errorMessage.includes(SESSION_TIMEOUT_PATTERN)) {
    return 'session_timeout';
  }
  if (errorMessage.includes('SessionNotFound')) {
    return 'session_not_found';
  }
  if (CONNECTIVITY_PATTERNS.some((p) => errorMessage.toLowerCase().includes(p.toLowerCase()))) {
    return 'connectivity';
  }
  return 'unknown';
}
